/**
 * SoftwareIQ Module API
 *
 * WHY: SoftwareIQ is the core SwA module handling findings, observations,
 * vetting workflows, and VEX/SBOM exports. This is the primary workflow
 * for DoD software assurance analysts.
 */

import { http } from '../core/client'
import type {
  Finding,
  CreateFindingPayload,
  Observation,
  ObservationListParams,
  VetObservationPayload,
  VetResult,
  VexDocument,
  SbomDocument,
  Scanner,
  ScanJob,
  ToolDefinition,
  ToolRun,
  RiskTrendPoint,
  MissionStats,
  ReportExportRequest,
  ReportCapabilities,
  BulkVetRequest,
  BulkVetResult,
  ObservationCounts,
  SourceDefinition,
  SourceCreatePayload,
  SourceUpdatePayload,
  StigCatalog,
  StigSelection,
  StigSelectionInput,
  StigSyncStatus,
  StigScanRequest,
  MissionProfile,
  MissionProfilePayload,
  MissionIntegration,
  MissionIntegrationPayload,
  WorkflowStepStatus,
} from '../core/types'

const buildQueryString = (params?: Record<string, string | number | boolean | undefined>) => {
  if (!params) return ''
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return
    search.set(key, String(value))
  })
  const query = search.toString()
  return query ? `?${query}` : ''
}

/**
 * SwA Findings API.
 * Findings are vetted observations promoted to official vulnerabilities.
 */
export const findingsApi = {
  /**
   * List findings for a specific mission.
   */
  list: (missionId: string) =>
    http.get<Finding[]>(`/swa/missions/${missionId}/findings`),

  /**
   * Get a specific finding.
   */
  get: (missionId: string, findingId: string) =>
    http.get<Finding>(`/swa/missions/${missionId}/findings/${findingId}`),

  /**
   * Create a new finding (typically from vetting workflow).
   * WHY: Finding creation is a critical audit event that affects
   * the mission's VEX document output.
   */
  create: (missionId: string, data: CreateFindingPayload) =>
    http.post<Finding>(`/swa/missions/${missionId}/findings`, data),
}

/**
 * Observations API (Vetting Workflow).
 * WHY: This is the core "Weapon System" of SoftwareIQ per the UI Expert.
 * Observations are raw scanner outputs awaiting SME vetting.
 */
export const observationsApi = {
  /**
   * List observations with optional filters.
   * WHY: The vetting queue needs to filter by status to show
   * pending items first, then allow review of historical decisions.
   *
   * NOTE: Backend returns { count, observations } but we unwrap to array
   * for frontend convenience. (TD-050 fix)
   */
  list: async (params?: ObservationListParams): Promise<Observation[]> => {
    const searchParams = new URLSearchParams()
    if (params?.missionId) searchParams.set('mission_id', params.missionId)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.scannerId) searchParams.set('scanner_id', params.scannerId)
    const query = searchParams.toString()
    const response = await http.get<{ count: number; observations: Observation[] }>(
      `/observations${query ? `?${query}` : ''}`
    )
    // Unwrap the response - backend returns { count, observations }
    return response.observations || []
  },

  /**
   * Get a specific observation with full details.
   */
  get: (id: string) => http.get<Observation>(`/observations/${id}`),

  /**
   * Submit a vetting decision for an observation.
   * WHY: This is THE critical action in the SME workflow. The vetting
   * decision determines if an observation becomes an official finding.
   * Per the Compliance Officer, we should capture state snapshots here.
   */
  vet: (id: string, data: VetObservationPayload) =>
    http.post<VetResult>(`/observations/${id}/vet`, data),

  /**
   * Bulk vet multiple observations at once (Phase 12).
   * WHY: Analysts need to vet hundreds of observations at once when
   * a scanner produces many false positives from a bad rule.
   */
  bulkVet: (data: BulkVetRequest) =>
    http.post<BulkVetResult>('/observations/bulk-vet', data),

  /**
   * Get observation counts by status.
   * Useful for showing badge counts in the UI.
   */
  getCounts: (missionId?: string) => {
    const query = missionId ? `?mission_id=${missionId}` : ''
    return http.get<ObservationCounts>(`/observations/count${query}`)
  },
}

/**
 * VEX (Vulnerability Exploitability eXchange) API.
 * WHY: VEX documents are the primary compliance output per CSAF 2.0.
 * DoD customers require these for software authorization.
 */
export const vexApi = {
  /**
   * Export VEX document for a mission.
   * Returns CSAF 2.0 compliant JSON.
   */
  export: (missionId: string) =>
    http.get<VexDocument>(`/swa/missions/${missionId}/vex`),

  /**
   * Export full CSAF 2.0 VEX document.
   */
  exportCsaf: (missionId: string) =>
    http.get<VexDocument>(`/swa/missions/${missionId}/vex/csaf`),
}

/**
 * SBOM (Software Bill of Materials) API.
 * WHY: SBOM is required for NIST 800-171 software supply chain compliance.
 */
export const sbomApi = {
  /**
   * Export SBOM for a mission in CycloneDX format.
   */
  exportCycloneDx: (missionId: string) =>
    http.get<SbomDocument>(`/swa/missions/${missionId}/sbom?format=cyclonedx`),

  /**
   * Export SBOM for a mission in SPDX format.
   */
  exportSpdx: (missionId: string) =>
    http.get<SbomDocument>(`/swa/missions/${missionId}/sbom?format=spdx`),
}

/**
 * Scanners API.
 * WHY: The Operational Cyber Lead mandated visibility into scanner health.
 * Analysts need to know if scanners are online and when they last ran.
 */
export const scannersApi = {
  /**
   * List all registered scanners.
   */
  list: () => http.get<Scanner[]>('/scanners'),

  /**
   * Get a specific scanner by ID.
   */
  get: (scannerId: string) => http.get<Scanner>(`/scanners/${scannerId}`),

  /**
   * Get recent scan jobs for a scanner.
   */
  getJobs: (scannerId: string) =>
    http.get<ScanJob[]>(`/scanners/${scannerId}/jobs`),

  /**
   * Get scan jobs for a specific mission.
   */
  getMissionJobs: (missionId: string) =>
    http.get<ScanJob[]>(`/missions/${missionId}/scans`),
}

/**
 * Tool Registry API.
 * WHY: External tool registry for Software Assurance scan execution.
 */
export const toolsApi = {
  /**
   * List tool definitions by category (optional).
   */
  list: (params?: { category?: string; active_only?: boolean }) =>
    http.get<ToolDefinition[]>(`/tools${buildQueryString(params)}`),

  /**
   * Get tool definition by ID.
   */
  get: (toolId: string) => http.get<ToolDefinition>(`/tools/${toolId}`),

  /**
   * Execute a tool run for a mission.
   */
  run: (toolId: string, data: { mission_id: string; target: Record<string, unknown>; config?: Record<string, unknown> }) =>
    http.post<ToolRun>(`/tools/${toolId}/runs`, data),

  /**
   * Update tool definition (endpoint + connection).
   */
  update: (toolId: string, data: Partial<ToolDefinition> & { connection?: Record<string, unknown> }) =>
    http.put<ToolDefinition>(`/tools/${toolId}`, data),
}

/**
 * Tool Runs API.
 */
export const toolRunsApi = {
  list: (params?: { mission_id?: string; tool_id?: string; status?: string; limit?: number }) =>
    http.get<ToolRun[]>(`/tools/runs${buildQueryString(params)}`),
  get: (runId: string) => http.get<ToolRun>(`/tools/runs/${runId}`),
  downloadArtifact: async (runId: string, artifactIndex: number): Promise<Blob> => {
    const response = await fetch(`/api/v1/tools/runs/${runId}/artifacts/${artifactIndex}`)
    if (!response.ok) {
      throw new Error(`Artifact download failed: ${response.statusText}`)
    }
    return response.blob()
  },
}

/**
 * Statistics API.
 * WHY: Dashboard visualization requires aggregated data for risk trends.
 * This resolves TD-034 by providing real data instead of mock generation.
 */
export const statsApi = {
  /**
   * Get risk trend data for a mission.
   * Returns daily aggregates of finding counts by severity over the last N days.
   */
  getTrends: (missionId: string, days: number = 30) =>
    http.get<RiskTrendPoint[]>(`/stats/missions/${missionId}/trends?days=${days}`),

  /**
   * Get comprehensive mission statistics.
   */
  getMissionStats: (missionId: string) =>
    http.get<MissionStats>(`/stats/missions/${missionId}`),
}

/**
 * Reports API (Phase 11).
 * WHY: Compliance Officer mandated PDF/CSV export for ATO packages.
 * These reports become official compliance evidence.
 */
export const reportsApi = {
  /**
   * Get available report capabilities.
   * WHY: Frontend needs to know if PDF generation is available.
   */
  getCapabilities: () =>
    http.get<ReportCapabilities>('/reports/capabilities'),

  /**
   * Export mission report as PDF.
   * Returns a Blob for download.
   *
   * @param missionId - Mission to generate report for
   * @param request - Optional chart image and classification
   */
  exportPdf: async (missionId: string, request?: ReportExportRequest): Promise<Blob> => {
    const response = await fetch(`/api/v1/reports/missions/${missionId}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Mission context header will be added by interceptor
      },
      body: JSON.stringify(request || {}),
    })

    if (!response.ok) {
      throw new Error(`Report generation failed: ${response.statusText}`)
    }

    return response.blob()
  },

  /**
   * Export mission report as HTML (for browser viewing).
   */
  exportHtml: async (missionId: string, classification?: string): Promise<string> => {
    const params = classification ? `?classification=${classification}` : ''
    const response = await fetch(`/api/v1/reports/missions/${missionId}/export/html${params}`)

    if (!response.ok) {
      throw new Error(`Report generation failed: ${response.statusText}`)
    }

    return response.text()
  },

  /**
   * Export findings as CSV.
   * Returns a Blob for download.
   */
  exportCsv: async (missionId: string): Promise<Blob> => {
    const response = await fetch(`/api/v1/reports/missions/${missionId}/export/csv`)

    if (!response.ok) {
      throw new Error(`CSV export failed: ${response.statusText}`)
    }

    return response.blob()
  },

  /**
   * Utility to trigger browser download of a blob.
   */
  downloadBlob: (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },
}

/**
 * Source Ingestion API (Phase 25.5).
 */
export const sourcesApi = {
  list: (params?: { mission_id?: string }) =>
    http.get<{ count: number; sources: SourceDefinition[] }>(`/sources${buildQueryString(params)}`),
  get: (sourceId: string) => http.get<SourceDefinition>(`/sources/${sourceId}`),
  create: (payload: SourceCreatePayload) => http.post<SourceDefinition>('/sources', payload),
  update: (sourceId: string, payload: SourceUpdatePayload) => http.put<SourceDefinition>(`/sources/${sourceId}`, payload),
}

/**
 * STIG Catalog + Applicability API (Phase 25.2).
 */
export const stigsApi = {
  listCatalog: () => http.get<StigCatalog[]>('/stigs/catalog'),
  syncCatalog: () => http.post<{ status: string; synced: number; source: string }>('/stigs/catalog/sync', {}),
  getSyncStatus: () => http.get<StigSyncStatus>('/stigs/catalog/sync/status'),
  listMissionSelections: (missionId: string) =>
    http.get<{ mission_id: string; count: number; selections: StigSelection[] }>(`/stigs/missions/${missionId}`),
  upsertSelection: (missionId: string, catalogId: string, payload: StigSelectionInput) =>
    http.put<StigSelection>(`/stigs/missions/${missionId}/${catalogId}`, payload),
  exportCkl: async (missionId: string, catalogId: string): Promise<Blob> => {
    const response = await fetch(`/api/v1/stigs/missions/${missionId}/${catalogId}/ckl/export`)
    if (!response.ok) {
      throw new Error(`CKL export failed: ${response.statusText}`)
    }
    return response.blob()
  },
  importCkl: async (missionId: string, catalogId: string, file: File): Promise<{ status: string; ckl_path: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`/api/v1/stigs/missions/${missionId}/${catalogId}/ckl/import`, {
      method: 'POST',
      body: formData,
    })
    if (!response.ok) {
      throw new Error(`CKL import failed: ${response.statusText}`)
    }
    return response.json()
  },
  runScans: (missionId: string, payload: StigScanRequest) =>
    http.post<{ mission_id: string; scanner_id: string; job_ids: string[]; submitted_at: string }>(
      `/stigs/missions/${missionId}/scan`,
      payload
    ),
}

/**
 * Mission Profile API.
 */
export const missionProfilesApi = {
  get: (missionId: string) => http.get<MissionProfile>(`/swa/missions/${missionId}/profile`),
  upsert: (missionId: string, payload: MissionProfilePayload) =>
    http.put<MissionProfile>(`/swa/missions/${missionId}/profile`, payload),
}

/**
 * Mission Integrations API.
 */
export const missionIntegrationsApi = {
  list: (missionId: string) =>
    http.get<{ count: number; integrations: MissionIntegration[] }>(`/swa/missions/${missionId}/integrations`),
  upsert: (missionId: string, integrationId: string, payload: MissionIntegrationPayload) =>
    http.put<MissionIntegration>(`/swa/missions/${missionId}/integrations/${integrationId}`, payload),
  remove: (missionId: string, integrationId: string) =>
    http.delete<void>(`/swa/missions/${missionId}/integrations/${integrationId}`),
}

/**
 * Workflow Step Tracking API.
 */
export const workflowStepsApi = {
  list: (missionId: string) =>
    http.get<{ count: number; steps: WorkflowStepStatus[] }>(`/swa/missions/${missionId}/workflow/steps`),
  update: (missionId: string, stepId: string, completed: boolean) =>
    http.put<WorkflowStepStatus>(`/swa/missions/${missionId}/workflow/steps/${stepId}`, { completed }),
}
